import React, { useState } from 'react';
import { Modal, Button, ListGroup, Alert, Nav, Tab } from 'react-bootstrap';
import {
  DiceType,
  DICE_DATA,
  getDiceName,
  getDiceDescription,
  getDicePrice,
  isDicePurchasable,
  getDiceMaxOwned,
  getDiceSellPrice,
  isDiceSellable
} from '../../models/DiceModel';
import { useLanguage } from '../../i18n';
import '../../styles/ModalStyles.css';
import GameModal from '../game/GameModal';
import { updateUserGroschen, updateUserOwnedDice } from '../../services/LocalStorageService';

interface DiceShopProps {
  show: boolean;
  onHide: () => void;
  playerGroschen: number;
  ownedDice: DiceType[];
  onBuyDice: (diceType: DiceType) => void;
  onSellDice: (diceType: DiceType) => void;
  currentConfig?: { diceConfigs: DiceType[] };
}

const DiceShop: React.FC<DiceShopProps> = ({
  show,
  onHide,
  playerGroschen,
  ownedDice,
  onBuyDice,
  onSellDice,
  currentConfig = { diceConfigs: [] }
}) => {
  const { t } = useLanguage();
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'danger'>('danger');
  const [confirmDice, setConfirmDice] = useState<DiceType | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'buy' | 'sell'>('buy');
  const [activeTab, setActiveTab] = useState<string>('buy');

  const handleBuyClick = (diceType: DiceType) => {
    const price = getDicePrice(diceType);
    const ownedCount = countOwnedDice(diceType);
    const maxOwned = getDiceMaxOwned(diceType);

    if (playerGroschen < price) {
      setMessage(t('insufficientGroschenBuy'));
      setMessageType('danger');
      return;
    }

    if (ownedCount >= maxOwned) {
      setMessage(t('maxOwnedReached', { name: getDiceName(diceType), max: maxOwned }));
      setMessageType('danger');
      return;
    }

    setConfirmDice(diceType);
    setConfirmAction('buy');
    setShowConfirm(true);
  };

  const handleSellClick = (diceType: DiceType) => {
    // 检查骰子是否正在使用中
    const usedCount = currentConfig.diceConfigs.filter(d => d === diceType).length;
    const ownedCount = ownedDice.filter(d => d === diceType).length;

    if (ownedCount <= usedCount) {
      setMessage(t('cannotSellInUse'));
      setMessageType('danger');
      return;
    }

    setConfirmDice(diceType);
    setConfirmAction('sell');
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    if (!confirmDice) return;

    if (confirmAction === 'buy') {
      onBuyDice(confirmDice);

      // 更新本地存储
      const price = getDicePrice(confirmDice);
      updateUserGroschen(playerGroschen - price);
      updateUserOwnedDice([...ownedDice, confirmDice]);

      setMessage(t('purchaseSuccess', { name: getDiceName(confirmDice) }));
      setMessageType('success');
    } else {
      onSellDice(confirmDice);
      setMessage(t('sellSuccess', { name: getDiceName(confirmDice), amount: getDiceSellPrice(confirmDice) }));
      setMessageType('success');
    }

    setShowConfirm(false);
    setConfirmDice(null);
  };

  // 过滤掉不可购买的骰子
  const shopDice = Object.values(DiceType).filter(type =>
    type !== DiceType.NORMAL && isDicePurchasable(type)
  );

  // 过滤可出售的骰子
  const sellableDice = Object.values(DiceType).filter(type =>
    type !== DiceType.NORMAL &&
    isDiceSellable(type) &&
    ownedDice.includes(type)
  );

  // 计算每种骰子已拥有的数量
  const countOwnedDice = (diceType: DiceType) => {
    return ownedDice.filter(type => type === diceType).length;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" className="kcd-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t('diceShop')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Tab.Container id="shop-tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'buy')}>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="buy">{t('buyDice')}</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="sell">{t('sellDice')}</Nav.Link>
                </Nav.Item>
              </Nav>
              <div className="ml-auto">
                <span style={{ fontSize: '0.9rem', color: '#666' }}>{t('availableGroschen')} <strong>{playerGroschen}</strong></span>
              </div>
            </Tab.Container>
          </div>

          {message && (
            <Alert variant={messageType} className="mb-3">
              {message}
            </Alert>
          )}

          <Tab.Content>
            <Tab.Pane eventKey="buy" active={activeTab === 'buy'}>
              <ListGroup className="mb-4">
                {shopDice.map((diceType) => {
                  const ownedCount = countOwnedDice(diceType);
                  const maxOwned = getDiceMaxOwned(diceType);
                  const isMaxReached = ownedCount >= maxOwned;

                  return (
                    <ListGroup.Item
                      key={diceType}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div className="d-flex align-items-center">
                        <div
                          style={{
                            backgroundColor: DICE_DATA[diceType].backgroundColor,
                            color: DICE_DATA[diceType].textColor,
                            width: '30px',
                            height: '30px',
                            borderRadius: '6px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginRight: '10px',
                            border: '1px solid #ccc'
                          }}
                        >
                          {diceType.includes('CAVIER') ? diceType.charAt(diceType.length - 1) : '?'}
                        </div>
                        <div>
                          <div className="font-weight-bold">{getDiceName(diceType)}</div>
                          <small className="text-muted">{getDiceDescription(diceType)}</small>
                          <div>
                            <small>{t('owned', { count: ownedCount, max: maxOwned })}</small>
                            {isMaxReached && <small className="text-danger ml-2">{t('maxReached')}</small>}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex flex-column align-items-end">
                        <span className="text-primary font-weight-bold">{getDicePrice(diceType)} {t('groschen')}</span>
                        <Button
                          variant="kcd"
                          size="sm"
                          className="mt-2 kcd-button"
                          onClick={() => handleBuyClick(diceType)}
                          disabled={playerGroschen < getDicePrice(diceType) || isMaxReached}
                        >
                          {isMaxReached ? t('maxReachedBtn') : t('buy')}
                        </Button>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Tab.Pane>

            <Tab.Pane eventKey="sell" active={activeTab === 'sell'}>
              <p className="text-muted small mb-3">{t('sellNote')}</p>
              <ListGroup className="mb-4">
                {sellableDice.map((diceType) => {
                  const count = ownedDice.filter(d => d === diceType).length;
                  const usedCount = currentConfig.diceConfigs.filter(d => d === diceType).length;

                  // 如果玩家没有这种骰子，不显示
                  if (count === 0) return null;

                  return (
                    <ListGroup.Item
                      key={diceType}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div className="d-flex align-items-center">
                        <div
                          style={{
                            backgroundColor: DICE_DATA[diceType].backgroundColor,
                            color: DICE_DATA[diceType].textColor,
                            width: '30px',
                            height: '30px',
                            borderRadius: '6px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginRight: '10px',
                            border: '1px solid #ccc'
                          }}
                        >
                          {diceType.includes('CAVIER') ? diceType.charAt(diceType.length - 1) : '?'}
                        </div>
                        <div>
                          <div className="font-weight-bold">{getDiceName(diceType)}</div>
                          <small className="text-muted">{getDiceDescription(diceType)}</small>
                          <div>
                            <small>{t('ownedCount', { count, used: usedCount })}</small>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex flex-column align-items-end">
                        <span className="text-success font-weight-bold">{getDiceSellPrice(diceType)} {t('groschen')}</span>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="mt-2"
                          disabled={count <= usedCount}
                          onClick={() => count > usedCount && handleSellClick(diceType)}
                        >
                          {count > usedCount ? t('sell') : t('inUse')}
                        </Button>
                      </div>
                    </ListGroup.Item>
                  );
                })}
                {sellableDice.length === 0 && (
                  <Alert variant="info">
                    {t('noSellableDice')}
                  </Alert>
                )}
              </ListGroup>
            </Tab.Pane>
          </Tab.Content>
        </Modal.Body>
        <Modal.Footer className="game-rules-footer">
          <Button variant="kcd" className="kcd-button" onClick={onHide}>
            {t('close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <GameModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        title={confirmAction === 'buy' ? t('confirmPurchase') : t('confirmSell')}
        content={confirmDice ?
          confirmAction === 'buy' ?
            t('confirmBuyText', { name: getDiceName(confirmDice), price: getDicePrice(confirmDice) }) :
            t('confirmSellText', { name: getDiceName(confirmDice), amount: getDiceSellPrice(confirmDice) })
          : ''
        }
        onConfirm={handleConfirm}
        showConfirmButton={true}
      />
    </>
  );
};

export default DiceShop;
